import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { logger } from '@/utils/logger'
import {
    type UTMParams,
    appendUTMParams,
    extractUTMParams,
    getStoredUTMParams,
    saveUTMParams,
    utmParamsToString
} from '@/utils/utmUtils'

/**
 * Custom hook to manage UTM parameters across the application
 * Automatically captures UTM params from URL and persists them
 */
export const useUTMParams = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const [utmParams, setUtmParams] = useState<UTMParams>({})

    // Capture UTM params on initial load or when URL changes
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search)
        const urlUTMParams = extractUTMParams(searchParams)

        // If URL has UTM params, save them
        if (Object.keys(urlUTMParams).length > 0) {
            logger.info('UTM Params captured from URL:', urlUTMParams)
            saveUTMParams(urlUTMParams)
            setUtmParams(urlUTMParams)
        } else {
            // Otherwise, load from storage
            const storedParams = getStoredUTMParams()

            if (Object.keys(storedParams).length > 0) {
                logger.debug('UTM Params loaded from storage:', storedParams)
            }
            setUtmParams(storedParams)

            // If we have stored params but URL doesn't have them, add them to URL
            if (Object.keys(storedParams).length > 0) {
                const currentUrl = location.pathname + location.search
                const newUrl = appendUTMParams(currentUrl, storedParams)

                if (currentUrl !== newUrl) {
                    // Replace current URL with UTM params without adding to history
                    navigate(newUrl, { replace: true })
                }
            }
        }
    }, [location.search, location.pathname, navigate])

    /**
     * Navigate to a path with UTM parameters appended
     */
    const navigateWithUTM = (path: string, options?: { replace?: boolean }) => {
        const urlWithUTM = appendUTMParams(path, utmParams)

        navigate(urlWithUTM, options)
    }

    /**
     * Get current UTM params as query string
     */
    const getUTMQueryString = (): string => {
        return utmParamsToString(utmParams)
    }

    /**
     * Append UTM params to any URL
     */
    const addUTMToUrl = (url: string): string => {
        return appendUTMParams(url, utmParams)
    }

    return {
        utmParams,
        navigateWithUTM,
        getUTMQueryString,
        addUTMToUrl,
        hasUTMParams: Object.keys(utmParams).length > 0
    }
}
